import { Link } from "react-router-dom";
import {
  useProductsCatalog,
  PRODUCT_STATUS_CLASS,
  PRODUCT_STATUS_LABEL,
  type ProductCatalogItem,
} from "@/hooks/useProductsCatalog";
import { selectHomeProducts } from "@/lib/homeShowcase";
import { Photo } from "@/components/shared/Photo";
import { cn } from "@/lib/utils";

export type ProductTeaserItem = Pick<
  ProductCatalogItem,
  "id" | "slug" | "name" | "status" | "description" | "pricing" | "image_url"
>;

export interface ProductsTeaserViewProps {
  /** Já filtrados e cortados pelo container. Vazio = seção não renderiza. */
  products: ProductTeaserItem[];
  className?: string;
}

/**
 * Prévia de "Produtos prontos" na home. Só apresentação: com lista vazia
 * não renderiza nada (a home não anuncia catálogo vazio).
 */
export function ProductsTeaserView({ products, className }: ProductsTeaserViewProps) {
  if (products.length === 0) return null;

  return (
    <section className={cn("mx-auto max-w-[1180px] px-8 pt-[70px] max-[900px]:px-5", className)}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[31px] font-bold tracking-[-0.03em]">Produtos prontos</h2>
          <p className="mt-3 text-[15.5px] text-muted-foreground">
            Sistemas que já construí e que você contrata como base — mais rápido que do zero.
          </p>
        </div>
        <Link to="/produtos" className="text-sm font-bold text-primary hover:text-primary-deep">
          Ver catálogo →
        </Link>
      </div>
      <div className="mt-7 grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-[18px]">
        {products.map((p) => (
          <Link
            key={p.id}
            to={`/produtos/${p.slug}`}
            className="block overflow-hidden rounded-[18px] border border-border bg-card shadow-card transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-primary"
          >
            <div className="aspect-[16/9] overflow-hidden border-b border-border">
              {p.image_url ? (
                <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
              ) : (
                <Photo id="notebook-mesa" decorative sizes="(max-width: 900px) 100vw, 380px" />
              )}
            </div>
            <div className="p-5">
              <span
                className={`inline-flex h-[23px] items-center rounded-[7px] px-2.5 text-[10.5px] font-bold ${PRODUCT_STATUS_CLASS[p.status]}`}
              >
                {PRODUCT_STATUS_LABEL[p.status]}
              </span>
              <h3 className="mt-3 text-[17px] font-bold tracking-[-0.02em]">{p.name}</h3>
              {p.description && (
                <p className="mt-2 line-clamp-2 text-[13.5px] leading-[1.6] text-muted-foreground">
                  {p.description}
                </p>
              )}
              {p.pricing && (
                <div className="mt-4 border-t border-border pt-3 text-[13px] font-bold">
                  {p.pricing.split("·")[0]?.trim()}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

/** Container do site: catálogo público → até 3 disponíveis/beta. */
const ProductsTeaser = () => {
  const { data } = useProductsCatalog();
  return <ProductsTeaserView products={selectHomeProducts(data)} />;
};

export default ProductsTeaser;
